// AI Service for Reptile Info Tablet
// Primary provider: GPT-OSS via Ollama (required by hackathon)
// Optional fallback: OpenAI API (disabled by default)

class AIService {
  constructor() {
    // Provider selection via env var
    // Values: 'ollama' (default, GPT-OSS) | 'openai' (optional fallback)
    this.provider = (process.env.REACT_APP_AI_PROVIDER || 'ollama').toLowerCase();

    // Ollama (GPT-OSS) config
    this.baseUrl = process.env.REACT_APP_OLLAMA_BASE || 'http://localhost:11434';
    this.model = process.env.REACT_APP_OLLAMA_MODEL || 'gpt-oss:20b';

    // OpenAI config (optional)
    this.openaiApiKey = process.env.REACT_APP_OPENAI_API_KEY || '';
    this.openaiModel = process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o-mini';

    this.isConnected = false;
    this.activeProvider = null;
    this.activeModel = null;
    // Only probe Ollama when provider is ollama AND app is running locally
    // Prevents noisy errors on production where localhost:11434 is unreachable
    const isLocalHost = (typeof window !== 'undefined') && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
    if (this.provider === 'ollama' && isLocalHost) {
      this.testConnection();
    } else {
      // Not probing Ollama (production or non-local) – treat Ollama as not connected
      this.isConnected = false;
    }
    // Track whether OpenAI can be used as a cloud provider
    this.openaiAvailable = !!this.openaiApiKey;
    // Optional Netlify proxy usage
    this.useProxy = (process.env.REACT_APP_USE_NETLIFY_PROXY || 'false').toLowerCase() === 'true';
  }

  // Test connection to Ollama
  async testConnection() {
    try {
      const response = await fetch(`${this.baseUrl}/api/tags`);
      if (response.ok) {
        this.isConnected = true;
        this.activeProvider = 'ollama';
        this.activeModel = this.model;
        console.log('✅ Connected to Ollama AI service');
      } else {
        this.isConnected = false;
        console.warn('⚠️ Ollama service not responding');
      }
    } catch (error) {
      this.isConnected = false;
      console.warn('⚠️ Cannot connect to Ollama service:', error.message);
    }
  }

  // Generate AI response for reptile care questions
  async generateResponse(question, speciesData = null) {
    const prompt = this.buildPrompt(question, speciesData);

    try {
      if (this.provider === 'ollama') {
        // Prefer Ollama locally; if unavailable and OpenAI key exists, fall back to OpenAI
        if (this.isConnected) {
          const response = await fetch(`${this.baseUrl}/api/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              model: this.model,
              prompt,
              stream: false,
              options: { temperature: 0.7, top_p: 0.9, max_tokens: 300 }
            })
          });
          if (!response.ok) throw new Error(`Ollama error: ${response.status}`);
          const data = await response.json();
          this.activeProvider = 'ollama';
          this.activeModel = this.model;
          return this.formatAIResponse(data.response);
        }

        // Ollama not connected → try OpenAI if available
        if (this.openaiApiKey) {
          const result = await this.requestOpenAIWithRetry(prompt);
          if (result && result.content) return this.formatAIResponse(result.content);
          // If still no content (e.g., repeated 429), surface a helpful message
          return {
            answer: 'We are getting a lot of questions right now and hit a rate limit. Please wait a few seconds and try again.',
            funFact: '💡 Pro Tip: You can also check the care guide above for instant answers while you wait.',
            source: 'Rate Limit'
          };
        }

        // No providers available
        return this.getFallbackResponse(question, speciesData);
      }

      if (this.provider === 'openai') {
        if (!this.openaiApiKey) return this.getFallbackResponse(question, speciesData);

        const result = await this.requestOpenAIWithRetry(prompt);
        if (result && result.content) return this.formatAIResponse(result.content);
        return {
          answer: 'We are currently rate limited by the AI provider. Please try again in a moment.',
          funFact: '💡 Fun fact: Many reptiles thermoregulate by moving between warm and cool spots!',
          source: 'Rate Limit'
        };
      }

      // Unknown provider → fallback
      return this.getFallbackResponse(question, speciesData);
    } catch (error) {
      console.error('AI service error:', error);
      return this.getFallbackResponse(question, speciesData);
    }
  }

  // Helper: sleep for retries
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Request OpenAI with basic exponential backoff on 429/5xx
  async requestOpenAIWithRetry(prompt, maxRetries = 2, initialDelayMs = 800) {
    let attempt = 0;
    let delay = initialDelayMs;
    // Support a prioritized, comma-separated model list. First that works wins.
    const modelList = (this.openaiModel || 'gpt-4o-mini,gpt-4o').split(',').map(m => m.trim()).filter(Boolean);
    while (attempt <= maxRetries) {
      try {
        const model = modelList[Math.min(attempt, modelList.length - 1)] || modelList[0];
        const resp = this.useProxy
          ? await fetch('/.netlify/functions/ask-ai', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ prompt })
            })
          : await fetch('https://api.openai.com/v1/chat/completions', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.openaiApiKey}`
              },
              body: JSON.stringify({
                model,
                messages: [
                  { role: 'system', content: 'You are a helpful reptile expert that follows strict safety rules.' },
                  { role: 'user', content: prompt }
                ],
                temperature: 0.7,
                // Slightly smaller response to reduce rate pressure
                max_tokens: 220
              })
            });
        if (resp.status === 429 || resp.status >= 500) {
          if (attempt === maxRetries) return null;
          await this.sleep(delay);
          delay = Math.min(delay * 2, 5000);
          attempt++;
          continue;
        }
        if (!resp.ok) throw new Error(`OpenAI error: ${resp.status}`);
        const json = await resp.json();
        const content = this.useProxy ? (json.content || '') : (json.choices?.[0]?.message?.content || '');
        this.activeProvider = 'openai';
        this.activeModel = model;
        return { content, modelUsed: model };
      } catch (err) {
        // Network errors → retry
        if (attempt === maxRetries) return null;
        await this.sleep(delay);
        delay = Math.min(delay * 2, 5000);
        attempt++;
      }
    }
    return null;
  }

  // Build intelligent prompt for the AI
  buildPrompt(question, speciesData) {
    let basePrompt = `You are a helpful reptile expert at Petco. Give concise, safe, and clear advice about reptile care. 
    
IMPORTANT SAFETY RULES:
- NEVER recommend feeding live prey to snakes
- NEVER suggest unsafe temperatures or humidity levels
- NEVER recommend dangerous substrates or tank setups
- Always prioritize animal safety and welfare
- If unsure, recommend consulting a veterinarian

Question: ${question}`;

    if (speciesData) {
      basePrompt += `

SPECIFIC SPECIES INFORMATION:
Name: ${speciesData.name}
Type: ${speciesData.type}
Habitat: ${JSON.stringify(speciesData.habitat)}
Diet: ${JSON.stringify(speciesData.diet)}
Care Tips: ${speciesData.careTips.join(', ')}
Warnings: ${speciesData.warnings.join(', ')}

Please provide advice specific to ${speciesData.name} based on the information above.`;
    }

    basePrompt += `

Please provide a helpful, accurate response in 2-3 sentences. Include one fun fact if relevant.`;

    return basePrompt;
  }

  // Format AI response for display
  formatAIResponse(response) {
    // Clean up the response
    let cleaned = response.trim();
    
    // Extract fun fact if present
    let funFact = null;
    if (cleaned.includes('Fun fact:') || cleaned.includes('Did you know:')) {
      const parts = cleaned.split(/Fun fact:|Did you know:/);
      if (parts.length > 1) {
        cleaned = parts[0].trim();
        funFact = parts[1].trim();
      }
    }

    return {
      answer: cleaned,
      funFact: funFact,
      source: 'AI Expert'
    };
  }

  // Fallback response when AI is not available
  getFallbackResponse(question, speciesData) {
    const fallbackResponses = {
      'feeding': 'For feeding questions, please refer to the care information above and consult with a Petco associate for specific guidance.',
      'temperature': 'Temperature requirements are listed in the habitat section above. Always use proper thermometers and heating equipment.',
      'humidity': 'Humidity levels are crucial for reptile health. Check the habitat requirements above and use hygrometers to monitor levels.',
      'substrate': 'Substrate recommendations are listed above. Avoid loose substrates for young reptiles and always research before use.',
      'handling': 'Handle reptiles gently and support their body fully. Wash hands before and after handling.',
      'default': 'This is a great question! Please refer to the care information above and feel free to ask a Petco associate for additional guidance.'
    };

    // Try to match question to fallback response
    const questionLower = question.toLowerCase();
    let response = fallbackResponses.default;
    
    for (const [key, value] of Object.entries(fallbackResponses)) {
      if (questionLower.includes(key)) {
        response = value;
        break;
      }
    }

    return {
      answer: response,
      funFact: '💡 Pro Tip: Always research thoroughly before bringing a new reptile friend home!',
      source: 'Care Guide'
    };
  }

  // Change AI model (Ollama only)
  async changeModel(modelName) {
    try {
      const response = await fetch(`${this.baseUrl}/api/tags`);
      if (response.ok) {
        const models = await response.json();
        const availableModel = models.models?.find(m => m.name.includes(modelName));
        if (availableModel) {
          this.model = availableModel.name;
          console.log(`✅ Switched to AI model: ${this.model}`);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error changing model:', error);
      return false;
    }
  }

  // Get connection status
  getStatus() {
    const openaiAvailable = !!this.openaiApiKey;
    // Consider the assistant "connected" if either Ollama is connected or OpenAI is available
    const connected = this.isConnected || openaiAvailable;
    return {
      connected,
      model: this.model,
      endpoint: this.baseUrl,
      provider: this.activeProvider || (this.isConnected ? 'ollama' : (openaiAvailable ? 'openai' : 'none')),
      activeModel: this.activeModel || (this.isConnected ? this.model : this.openaiModel)
    };
  }
}

export default new AIService();
